import React, { useMemo, useState } from 'react';
import { modalStyles as styles } from './modalStyles';
import { useAuth } from '@clerk/clerk-react';
import { colors } from '../theme';
import SubmissionImageGallery from './SubmissionImageGallery';
import RmaCaseImages from './RmaCaseImages';
import RmaCustomerSummary from './RmaCustomerSummary';
import CloseCaseAction from './CloseCaseAction';
import RmaDeviceList from './RmaDeviceList.jsx';

const API_BASE = process.env.REACT_APP_API_URL;

const KNOWN_ACCESSORY_LABELS = {
  ac_power_cable: 'AC power cable',
  hdmi_cable: 'HDMI cable',
  dp_cable: 'DP cable',
  touch_cable: 'Touch cable (USB A–USB B)'
};

const PARTIAL_TYPES = ['ELU', 'DIU'];
const PARTIAL_TYPE_LABELS = {
  ELU: 'ELU only',
  DIU: 'DIU only'
};
const REQUIRED_MOUNT_OPTIONS = ['HINGE', 'PANEL_MOUNT'];

const normalizePartialType = value => {
  if (value === null || typeof value === 'undefined') {
    return '';
  }
  const trimmed = value.toString().trim();
  if (!trimmed) {
    return '';
  }
  const canonical = trimmed.replace(/[^A-Z0-9]/gi, '').toUpperCase();
  if (PARTIAL_TYPES.includes(canonical)) {
    return canonical;
  }
  return '';
};

const describeDeliveryState = (isPartial, partialType) => {
  if (!isPartial) {
    return 'Complete set (ELU + DIU)';
  }
  const canonical = normalizePartialType(partialType);
  return canonical ? `Partial (${PARTIAL_TYPE_LABELS[canonical] || canonical})` : 'Partial delivery';
};

const normalizeMountingOption = value => {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  const canonical = trimmed
    .replace(/\s+/g, '_')
    .replace(/-+/g, '_')
    .replace(/__+/g, '_')
    .toUpperCase();
  return canonical === 'NONE' ? '' : canonical;
};

const formatMountLabel = value => {
  if (!value) return 'Not specified';
  return value.replace(/_/g, ' ');
};

const canonicalAccessoryCode = value => {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  return trimmed
    .replace(/qty$/i, '')
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/__+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase();
};

const formatAccessoryLabel = code => {
  const canonical = canonicalAccessoryCode(code);
  if (canonical && KNOWN_ACCESSORY_LABELS[canonical]) {
    return KNOWN_ACCESSORY_LABELS[canonical];
  }
  const source = typeof code === 'string' && code.trim() ? code.trim() : canonical;
  if (!source) return 'Accessory';
  return source
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b([a-z])/g, char => char.toUpperCase());
};

const aggregateAccessoryQuantities = (list, { treatIncluded = false } = {}) => {
  const map = new Map();
  list.forEach(item => {
    if (!item || !item.canonical) return;
    const existing = map.get(item.canonical) || { qty: 0, label: item.label };
    const qty = treatIncluded ? (item.included ? Math.max(0, Number(item.qty) || 0) : 0) : Math.max(0, Number(item.qty) || 0);
    existing.qty += qty;
    if (!existing.label && item.label) {
      existing.label = item.label;
    }
    map.set(item.canonical, existing);
  });
  return map;
};

const computeAccessoriesDelta = (expectedList, foundList) => {
  const expectedMap = aggregateAccessoryQuantities(expectedList);
  const foundMap = aggregateAccessoryQuantities(foundList, { treatIncluded: true });
  const missing = [];
  const extra = [];

  const codes = new Set([...expectedMap.keys(), ...foundMap.keys()]);
  codes.forEach(code => {
    const expectedEntry = expectedMap.get(code) || { qty: 0, label: formatAccessoryLabel(code) };
    const foundEntry = foundMap.get(code) || { qty: 0, label: expectedEntry.label };
    const expectedQty = expectedEntry.qty || 0;
    const foundQty = foundEntry.qty || 0;
    if (expectedQty > foundQty) {
      missing.push({ code, qty: expectedQty - foundQty, label: expectedEntry.label });
    }
    if (foundQty > expectedQty) {
      extra.push({ code, qty: foundQty - expectedQty, label: foundEntry.label });
    }
  });

  return { missing, extra };
};

const formatAccessorySummary = entries => {
  if (!entries || entries.length === 0) return null;
  return entries
    .map(entry => (entry.qty && entry.qty > 1 ? `${entry.label} (x${entry.qty})` : entry.label))
    .join(', ');
};

const computeDiff = (expected, found) => {
  const expectedSerial = (expected.mainDeviceSerial || '').trim();
  const foundSerial = (found.mainDeviceSerial || '').trim();
  const serialMatch = expectedSerial ? expectedSerial === foundSerial : !foundSerial;

  const expectedMount = normalizeMountingOption(expected.mountingOption);
  const foundMount = normalizeMountingOption(found.mountingOption);
  const mountMatch = expectedMount === foundMount;

  const expectedHmi = typeof expected.hmiIncluded === 'boolean' ? expected.hmiIncluded : null;
  const foundHmi = typeof found.hmiIncluded === 'boolean' ? found.hmiIncluded : null;
  const hmiMatch = expectedHmi === null || expectedHmi === foundHmi;

  const expectedAccessories = Array.isArray(expected.accessories)
    ? expected.accessories.map(item => ({
        canonical: canonicalAccessoryCode(item.code),
        qty: Number(item.qty) || 0,
        label: formatAccessoryLabel(item.code)
      }))
    : [];

  const foundAccessories = Array.isArray(found.accessories)
    ? found.accessories.map(item => ({
        canonical: canonicalAccessoryCode(item.code),
        qty: Number(item.qty) || 0,
        included: item.included !== false,
        label: formatAccessoryLabel(item.code)
      }))
    : [];

  const accessoryDiff = computeAccessoriesDelta(expectedAccessories, foundAccessories);

  return {
    serialMatch,
    mountMatch,
    hmiMatch,
    accessories: accessoryDiff
  };
};

const extractMountOptions = (rma, expectedMount, foundMount) => {
  const options = [];
  const seen = new Set();
  const addOption = value => {
    const normalized = normalizeMountingOption(value);
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    options.push({ value: normalized, label: formatMountLabel(normalized) });
  };

  const candidateArrays = [
    rma?.productConfiguration?.mountingOptions,
    rma?.productConfiguration?.mountOptions,
    rma?.productDefinition?.mountingOptions,
    rma?.productDefinition?.mountOptions,
    rma?.productProfile?.mountingOptions,
    rma?.productProfile?.mountOptions,
    rma?.productData?.mountingOptions,
    rma?.productData?.mountOptions,
    rma?.expectedReturn?.mountingOptions,
    rma?.expectedReturn?.mountOptions,
    rma?.expectedReturn?.mount?.options,
    rma?.expectedReturn?.mount?.allowedOptions
  ];

  candidateArrays.forEach(list => {
    if (Array.isArray(list)) {
      list.forEach(addOption);
    }
  });

  [expectedMount, foundMount, rma?.receipt?.expected?.mountingOption].forEach(addOption);

  REQUIRED_MOUNT_OPTIONS.forEach(addOption);

  return options;
};

const deriveExpectedInfo = rma => {
  const expectedReturn = rma?.expectedReturn || {};
  const receiptExpected = rma?.receipt?.expected || {};
  const expectedDeliverySource =
    (expectedReturn?.delivery && typeof expectedReturn.delivery === 'object' ? expectedReturn.delivery : null) ||
    (receiptExpected.delivery && typeof receiptExpected.delivery === 'object' ? receiptExpected.delivery : null) ||
    {};
  const expectedDeliveryIsPartial = expectedDeliverySource.isPartial === true;
  const expectedDeliveryType = normalizePartialType(expectedDeliverySource.partialType);

  const receiptMountOption =
    receiptExpected?.mounting?.option || receiptExpected?.mountingOption || '';
  const mountType = receiptMountOption || expectedReturn?.mount?.type || expectedReturn?.mountingOption || '';
  const receiptHmi =
    typeof receiptExpected?.mounting?.hmiIncluded === 'boolean'
      ? receiptExpected.mounting.hmiIncluded
      : typeof receiptExpected?.hmiIncluded === 'boolean'
      ? receiptExpected.hmiIncluded
      : null;
  const hmiIncluded =
    typeof receiptHmi === 'boolean'
      ? receiptHmi
      : typeof expectedReturn?.mount?.hmi === 'boolean'
      ? expectedReturn.mount.hmi
      : null;
  const accessories = new Map();

  const sourceAccessories = expectedReturn?.accessories;
  if (Array.isArray(sourceAccessories)) {
    sourceAccessories.forEach(entry => {
      if (!entry || typeof entry !== 'object') return;
      const canonical = canonicalAccessoryCode(entry.code || '');
      if (!canonical) return;
      const qty = Math.max(0, Number(entry.qty || entry.quantity || entry.count) || 0);
      accessories.set(canonical, {
        code: entry.code || canonical,
        label: formatAccessoryLabel(entry.code || canonical),
        expectedQty: qty
      });
    });
  } else if (sourceAccessories && typeof sourceAccessories === 'object') {
    Object.entries(sourceAccessories).forEach(([key, qty]) => {
      const canonical = canonicalAccessoryCode(key);
      if (!canonical) return;
      const expectedQty = Math.max(0, Number(qty) || 0);
      accessories.set(canonical, {
        code: canonical,
        label: formatAccessoryLabel(key),
        expectedQty
      });
    });
  }

  const receiptExpectedAccessories = Array.isArray(rma?.receipt?.expected?.accessories)
    ? rma.receipt.expected.accessories
    : [];
  receiptExpectedAccessories.forEach(entry => {
    const canonical = canonicalAccessoryCode(entry.code || '');
    if (!canonical) return;
    const expectedQty = Math.max(0, Number(entry.qty) || 0);
    if (!accessories.has(canonical)) {
      accessories.set(canonical, {
        code: entry.code || canonical,
        label: formatAccessoryLabel(entry.code || canonical),
        expectedQty
      });
    }
  });

  const accessoriesForDisplay = Array.from(accessories.values());

  const normalizedMount = normalizeMountingOption(mountType);

  return {
    mainDeviceSerial: rma?.serialNumber || '',
    mountingOption: normalizedMount || '',
    hmiIncluded,
    delivery: {
      isPartial: expectedDeliveryIsPartial,
      partialType: expectedDeliveryIsPartial ? expectedDeliveryType || null : null
    },
    mounting: {
      hasMounting: Boolean(normalizedMount),
      option: normalizedMount || null,
      hmiIncluded: typeof hmiIncluded === 'boolean' ? hmiIncluded : false
    },
    accessories: accessoriesForDisplay.map(item => ({ code: item.code, qty: item.expectedQty })),
    accessoriesForDisplay
  };
};

const buildInitialAccessoryRows = (expectedRows, foundRows) => {
  const rows = [];
  const map = new Map();
  let counter = 0;

  expectedRows.forEach(item => {
    const canonical = canonicalAccessoryCode(item.code);
    const id = `expected-${canonical || counter}`;
    const base = {
      id,
      code: item.code,
      label: item.label || formatAccessoryLabel(item.code),
      expectedQty: item.expectedQty,
      included: item.expectedQty > 0,
      qty: item.expectedQty > 0 ? item.expectedQty : 0,
      fromExpected: true
    };
    rows.push(base);
    if (canonical) {
      map.set(canonical, base);
    }
    counter += 1;
  });

  foundRows.forEach((entry, index) => {
    if (!entry || typeof entry !== 'object') return;
    const rawCode = typeof entry.code === 'string' ? entry.code.trim() : '';
    const canonical = canonicalAccessoryCode(rawCode || entry.code || '');
    const qty = Math.max(0, Number(entry.qty) || 0);
    const included = entry.included !== false && qty > 0;
    const label = formatAccessoryLabel(rawCode || entry.code || '');
    if (canonical && map.has(canonical)) {
      const row = map.get(canonical);
      row.code = rawCode || row.code;
      row.label = label;
      row.included = included;
      row.qty = included ? qty : 0;
    } else {
      rows.push({
        id: `found-${index}-${Date.now()}`,
        code: rawCode || entry.code || '',
        label,
        expectedQty: 0,
        included,
        qty: included ? qty : 0,
        fromExpected: false
      });
    }
  });

  return rows;
};

const sanitizeAccessoryPayload = rows =>
  rows
    .map(row => {
      const code = typeof row.code === 'string' ? row.code.trim() : '';
      if (!code) {
        return null;
      }
      const qty = Math.max(0, Number(row.qty) || 0);
      const included = row.included && qty > 0;
      return {
        code,
        included,
        qty: included ? qty : 0
      };
    })
    .filter(Boolean);

export default function RmaModalReceived({ rma, onClose, onSave }) {
  const { getToken } = useAuth();
  const expectedInfo = useMemo(() => deriveExpectedInfo(rma), [rma]);
  const initialFound = useMemo(() => rma?.receipt?.found || {}, [rma]);
  const initialFoundAccessories = useMemo(
    () => (Array.isArray(initialFound.accessories) ? initialFound.accessories : []),
    [initialFound]
  );
  const expectedAccessories = useMemo(
    () =>
      expectedInfo.accessoriesForDisplay.map(item => ({
        code: item.code,
        label: item.label,
        expectedQty: item.expectedQty
      })),
    [expectedInfo.accessoriesForDisplay]
  );

  const expectedMountOption = expectedInfo.mounting?.option || expectedInfo.mountingOption;

  const [foundSerial, setFoundSerial] = useState(
    initialFound.mainDeviceSerial || expectedInfo.mainDeviceSerial || ''
  );

  const initialDelivery = initialFound.delivery || {};
  const initialIsPartial = initialDelivery.isPartial === true;
  const initialPartialType = initialIsPartial
    ? normalizePartialType(initialDelivery.partialType || initialFound.partialType)
    : '';
  const [deliveryIsPartial, setDeliveryIsPartial] = useState(initialIsPartial);
  const [deliveryPartialType, setDeliveryPartialType] = useState(
    initialIsPartial ? initialPartialType || '' : ''
  );

  const initialMounting = initialFound.mounting || {};
  const fallbackMountOption =
    normalizeMountingOption(
      initialMounting.option ||
        initialFound.mountingOption ||
        expectedMountOption
    ) || '';
  const derivedHasMounting =
    typeof initialMounting.hasMounting === 'boolean'
      ? initialMounting.hasMounting
      : Boolean(fallbackMountOption);
  const [hasMounting, setHasMounting] = useState(derivedHasMounting);
  const [foundMount, setFoundMount] = useState(derivedHasMounting ? fallbackMountOption : '');

  const initialHmi =
    typeof initialMounting.hmiIncluded === 'boolean'
      ? initialMounting.hmiIncluded
      : typeof initialFound.hmiIncluded === 'boolean'
      ? initialFound.hmiIncluded
      : typeof expectedInfo.mounting?.hmiIncluded === 'boolean'
      ? expectedInfo.mounting.hmiIncluded
      : false;
  const [hmiIncluded, setHmiIncluded] = useState(initialHmi);

  const initialAccessoryRows = useMemo(
    () => buildInitialAccessoryRows(expectedAccessories, initialFoundAccessories),
    [expectedAccessories, initialFoundAccessories]
  );
  const [accessoryRows, setAccessoryRows] = useState(initialAccessoryRows);
  const [accessoriesPresence, setAccessoriesPresence] = useState(() => {
    if (initialFoundAccessories.length > 0) {
      return 'yes';
    }
    return initialAccessoryRows.length > 0 ? 'yes' : 'no';
  });
  const [missingRows, setMissingRows] = useState(() =>
    (rma?.receipt?.missingItems || []).map((item, index) => ({
      id: `missing-${index}`,
      name: item.name || '',
      qty: item.qty || 1,
      note: item.note || ''
    }))
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const mountOptions = useMemo(
    () => extractMountOptions(rma, expectedMountOption, foundMount),
    [rma, expectedMountOption, foundMount]
  );

  const accessoriesForDiff = useMemo(
    () =>
      accessoriesPresence === 'yes'
        ? accessoryRows.map(row => ({
            code: row.code,
            included: row.included,
            qty: row.qty
          }))
        : [],
    [accessoriesPresence, accessoryRows]
  );

  const effectiveMount = hasMounting ? foundMount : '';

  const expectedDelivery = expectedInfo.delivery || { isPartial: false, partialType: null };
  const expectedDeliveryType = normalizePartialType(expectedDelivery.partialType);
  const deliveryMatches =
    expectedDelivery.isPartial === deliveryIsPartial &&
    (!deliveryIsPartial || expectedDeliveryType === (deliveryPartialType || ''));
  const expectedDeliveryDescription = describeDeliveryState(
    expectedDelivery.isPartial,
    expectedDelivery.partialType
  );
  const foundDeliveryDescription = describeDeliveryState(deliveryIsPartial, deliveryPartialType);

  const diff = useMemo(
    () =>
      computeDiff(
        {
          mainDeviceSerial: expectedInfo.mainDeviceSerial,
          mountingOption: expectedMountOption,
          hmiIncluded: expectedInfo.hmiIncluded,
          accessories: expectedInfo.accessories
        },
        {
          mainDeviceSerial: foundSerial,
          mountingOption: effectiveMount,
          hmiIncluded,
          accessories: accessoriesForDiff
        }
      ),
    [expectedInfo, expectedMountOption, foundSerial, effectiveMount, hmiIncluded, accessoriesForDiff]
  );

  const accessoriesMissing = formatAccessorySummary(diff.accessories.missing);
  const accessoriesExtra = formatAccessorySummary(diff.accessories.extra);

  const handleAccessoryChange = (id, updates) => {
    setAccessoryRows(prev =>
      prev.map(row => {
        if (row.id !== id) return row;
        const next = { ...row, ...updates };
        if ('code' in updates) {
          next.label = formatAccessoryLabel(updates.code);
        }
        if ('included' in updates) {
          if (updates.included) {
            const fallbackQty = next.qty || next.expectedQty || 1;
            next.qty = fallbackQty;
          } else {
            next.qty = 0;
          }
        }
        if ('qty' in updates) {
          const parsed = Math.max(0, Number(updates.qty) || 0);
          next.qty = parsed;
        }
        return next;
      })
    );
  };

  const handleAddAccessory = () => {
    setAccessoryRows(prev => [
      ...prev,
      {
        id: `new-${Date.now()}-${prev.length}`,
        code: '',
        label: '',
        expectedQty: 0,
        included: false,
        qty: 0,
        fromExpected: false
      }
    ]);
  };

  const handleRemoveAccessory = id => {
    setAccessoryRows(prev => prev.filter(row => row.id !== id));
  };

  const handleAddMissingItem = () => {
    setMissingRows(prev => [...prev, { id: `missing-${Date.now()}`, name: '', qty: 1, note: '' }]);
  };

  const handleMissingItemChange = (id, field, value) => {
    setMissingRows(prev => prev.map(row => (row.id === id ? { ...row, [field]: value } : row)));
  };

  const handleRemoveMissingItem = id => {
    setMissingRows(prev => prev.filter(row => row.id !== id));
  };

  const handleConfirm = async () => {
    setError('');
    if (deliveryIsPartial && !deliveryPartialType) {
      setError('Select which device arrived in the partial delivery.');
      return;
    }
    if (hasMounting && !foundMount) {
      setError('Please select a mounting option.');
      return;
    }

    const accessoriesPayload =
      accessoriesPresence === 'yes' ? sanitizeAccessoryPayload(accessoryRows) : [];

    const missingItemsPayload = missingRows
      .map(row => ({
        name: (row.name || '').trim(),
        qty: Math.max(1, Math.round(Number(row.qty) || 1)),
        note: (row.note || '').trim()
      }))
      .filter(row => row.name);

    const payload = {
      found: {
        mainDeviceSerial: (foundSerial || '').trim(),
        delivery: {
          isPartial: deliveryIsPartial,
          partialType: deliveryIsPartial ? deliveryPartialType || null : null
        },
        mounting: {
          hasMounting,
          option: hasMounting ? foundMount : null,
          hmiIncluded
        },
        accessories: accessoriesPayload,
        mountingOption: hasMounting ? foundMount : '',
        hmiIncluded
      },
      missingItems: missingItemsPayload
    };

    try {
      setSaving(true);
      const token = await getToken();
      const res = await fetch(`${API_BASE}/rma/${rma._id}/received/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const message = body?.message || `Server responded with ${res.status}`;
        throw new Error(message);
      }
      const data = await res.json();
      if (!data.ok) {
        throw new Error(data.message || 'Failed to confirm receipt');
      }
      onSave(data.rma || rma);
    } catch (err) {
      console.error('Failed to confirm intake', err);
      setError(err.message || 'Failed to save intake. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <h2>Device Receipt</h2>
        <RmaCustomerSummary rma={rma} />
        <p style={{ color: colors.textMuted, marginBottom: '0.5rem' }}>
          <strong>Service Tag:</strong> {rma.serialNumber}{' '}
          <span style={{ marginLeft: '0.75rem' }}><strong>Product:</strong> {rma.productNumber || 'N/A'}</span>
        </p>

        <RmaDeviceList rma={rma} title="Check every unit in this shipment" />

        <section style={sectionStyles.container}>
          <h3 style={sectionStyles.heading}>What we expected</h3>
          <ul style={sectionStyles.list}>
            <li>
              <strong>Main device serial:</strong> {expectedInfo.mainDeviceSerial || 'Not specified'}
            </li>
            <li>
              <strong>Delivery:</strong> {describeDeliveryState(
                expectedInfo.delivery?.isPartial,
                expectedInfo.delivery?.partialType
              )}
            </li>
            <li>
              <strong>Mounting option:</strong> {expectedInfo.mountingOption ? formatMountLabel(normalizeMountingOption(expectedInfo.mountingOption)) : 'Not specified'}
            </li>
            <li>
              <strong>HMI included:</strong>{' '}
              {typeof expectedInfo.hmiIncluded === 'boolean' ? (expectedInfo.hmiIncluded ? 'Yes' : 'No') : 'Not specified'}
            </li>
            <li>
              <strong>Accessories:</strong>{' '}
              {expectedAccessories.length ? (
                <span>
                  {expectedAccessories
                    .map(item =>
                      item.expectedQty > 1
                        ? `${item.label} (x${item.expectedQty})`
                        : item.label
                    )
                    .join(', ')}
                </span>
              ) : (
                <span>None specified</span>
              )}
            </li>
          </ul>
        </section>

        <section style={sectionStyles.container}>
          <h3 style={sectionStyles.heading}>What arrived</h3>
          <label style={sectionStyles.label} htmlFor="received-main-serial">
            Main device serial
          </label>
          <input
            type="text"
            value={foundSerial}
            onChange={event => setFoundSerial(event.target.value)}
            id="received-main-serial"
            style={sectionStyles.input}
            placeholder="Serial on the device"
          />

          <label style={sectionStyles.checkboxLabel}>
            <input
              type="checkbox"
              checked={deliveryIsPartial}
              onChange={event => {
                const next = event.target.checked;
                setDeliveryIsPartial(next);
                if (!next) {
                  setDeliveryPartialType('');
                }
              }}
            />
            Partial delivery
          </label>
          {!deliveryIsPartial && (
            <div style={sectionStyles.helperText}>Complete set (ELU + DIU) assumed.</div>
          )}
          {deliveryIsPartial && (
            <>
              <label style={sectionStyles.label} htmlFor="partial-type-select">
                Partial type
              </label>
              <select
                value={deliveryPartialType}
                onChange={event => setDeliveryPartialType(normalizePartialType(event.target.value))}
                id="partial-type-select"
                style={sectionStyles.input}
              >
                <option value="">Select device</option>
                {PARTIAL_TYPES.map(type => (
                  <option key={type} value={type}>
                    {PARTIAL_TYPE_LABELS[type] || type}
                  </option>
                ))}
              </select>
            </>
          )}

          <label style={sectionStyles.checkboxLabel}>
            <input
              type="checkbox"
              checked={hasMounting}
              onChange={event => {
                const next = event.target.checked;
                setHasMounting(next);
                if (!next) {
                  setFoundMount('');
                  setHmiIncluded(false);
                } else if (!foundMount) {
                  const fallback = fallbackMountOption || (mountOptions[0]?.value || '');
                  setFoundMount(fallback);
                }
              }}
            />
            This unit has a mounting option
          </label>
          {hasMounting && (
            <>
              <label style={sectionStyles.label} htmlFor="mounting-option-select">
                Mounting option
              </label>
              <select
                value={foundMount}
                onChange={event => setFoundMount(event.target.value)}
                id="mounting-option-select"
                style={sectionStyles.input}
              >
                <option value="">Select option</option>
                {mountOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <label style={sectionStyles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={hmiIncluded}
                  onChange={event => setHmiIncluded(event.target.checked)}
                />
                Has HMI
              </label>
            </>
          )}

          <label style={sectionStyles.label} htmlFor="accessories-present-select">
            Accessories present?
          </label>
          <select
            value={accessoriesPresence}
            onChange={event => setAccessoriesPresence(event.target.value)}
            id="accessories-present-select"
            style={sectionStyles.input}
          >
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>

          {accessoriesPresence === 'yes' ? (
            <>
              <label style={{ ...sectionStyles.label, marginTop: '0.5rem' }}>Accessories received</label>
              <div style={sectionStyles.accessoryTable}>
                <div style={sectionStyles.accessoryHeader}>
                  <span>Accessory code</span>
                  <span>Expected</span>
                  <span>Included</span>
                  <span>Qty</span>
                  <span aria-hidden="true" />
                </div>
                {accessoryRows.length === 0 && (
                  <div style={sectionStyles.accessoryEmpty}>No accessories listed.</div>
                )}
                {accessoryRows.map(row => (
                  <div key={row.id} style={sectionStyles.accessoryRow}>
                    <div style={sectionStyles.accessoryCell}>
                      <input
                        type="text"
                        value={row.code}
                        onChange={event => handleAccessoryChange(row.id, { code: event.target.value })}
                        style={sectionStyles.codeInput}
                        placeholder="e.g. PowerCable"
                      />
                      {row.label && (
                        <span style={sectionStyles.accessoryHint}>{row.label}</span>
                      )}
                    </div>
                    <div style={sectionStyles.accessoryCell}>
                      {row.expectedQty > 0 ? `x${row.expectedQty}` : '—'}
                    </div>
                    <div style={sectionStyles.accessoryCell}>
                      <select
                        value={row.included ? 'yes' : 'no'}
                        onChange={event => handleAccessoryChange(row.id, { included: event.target.value === 'yes' })}
                        style={sectionStyles.selectInput}
                      >
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    </div>
                    <div style={sectionStyles.accessoryCell}>
                      <input
                        type="number"
                        min="0"
                        value={row.qty}
                        disabled={!row.included}
                        onChange={event => handleAccessoryChange(row.id, { qty: event.target.value })}
                        style={{ ...sectionStyles.qtyInput, opacity: row.included ? 1 : 0.6 }}
                      />
                    </div>
                    <div style={sectionStyles.removeCell}>
                      {!row.fromExpected && (
                        <button
                          type="button"
                          onClick={() => handleRemoveAccessory(row.id)}
                          style={sectionStyles.removeButton}
                          aria-label="Remove accessory"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <button type="button" onClick={handleAddAccessory} style={sectionStyles.addButton}>
                + Add accessory
              </button>
            </>
          ) : (
            <div style={sectionStyles.helperText}>No accessories recorded for this intake.</div>
          )}
        </section>

        <section style={sectionStyles.container}>
          <h3 style={sectionStyles.heading}>Missing / forgotten items</h3>
          <div style={sectionStyles.helperText}>
            Log anything the customer forgot to send (e.g. cable cover). It is recorded on the case and shown in the history.
          </div>
          {missingRows.map(row => (
            <div key={row.id} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.4rem' }}>
              <input
                type="text"
                value={row.name}
                onChange={e => handleMissingItemChange(row.id, 'name', e.target.value)}
                placeholder="Item (e.g. cable cover)"
                style={{ ...sectionStyles.input, flex: 2 }}
              />
              <input
                type="number"
                min={1}
                value={row.qty}
                onChange={e => handleMissingItemChange(row.id, 'qty', e.target.value)}
                style={{ ...sectionStyles.input, width: '4.5rem', flex: 'none', textAlign: 'center' }}
              />
              <input
                type="text"
                value={row.note}
                onChange={e => handleMissingItemChange(row.id, 'note', e.target.value)}
                placeholder="Note (optional)"
                style={{ ...sectionStyles.input, flex: 2 }}
              />
              <button
                type="button"
                onClick={() => handleRemoveMissingItem(row.id)}
                style={sectionStyles.removeButton}
                aria-label="Remove missing item"
              >
                ×
              </button>
            </div>
          ))}
          <button type="button" onClick={handleAddMissingItem} style={sectionStyles.addButton}>
            + Log missing item
          </button>
        </section>

        <section style={sectionStyles.container}>
          <h3 style={sectionStyles.heading}>Differences vs. expected</h3>
          <div style={{ ...diffStyles.row, alignItems: 'flex-start', flexDirection: 'column' }}>
            <span style={diffStyles.label}>Delivery</span>
            <span style={deliveryMatches ? diffStyles.good : diffStyles.bad}>
              {deliveryMatches ? 'Matches expected' : 'Mismatch'}
            </span>
            {!deliveryMatches && (
              <div style={diffStyles.detail}>
                Found {foundDeliveryDescription}. Expected {expectedDeliveryDescription}.
              </div>
            )}
          </div>
          <div style={diffStyles.row}>
            <span style={diffStyles.label}>Main device serial</span>
            <span style={diff.serialMatch ? diffStyles.good : diffStyles.bad}>
              {diff.serialMatch ? 'Matches expected' : 'Mismatch'}
            </span>
          </div>
          <div style={diffStyles.row}>
            <span style={diffStyles.label}>Mounting option</span>
            <span style={diff.mountMatch ? diffStyles.good : diffStyles.bad}>
              {diff.mountMatch ? 'Matches expected' : 'Mismatch'}
            </span>
          </div>
          <div style={diffStyles.row}>
            <span style={diffStyles.label}>HMI included</span>
            <span style={diff.hmiMatch ? diffStyles.good : diffStyles.bad}>
              {diff.hmiMatch ? 'Matches expected' : 'Mismatch'}
            </span>
          </div>
          <div style={{ ...diffStyles.row, alignItems: 'flex-start', flexDirection: 'column' }}>
            <span style={diffStyles.label}>Accessories</span>
            <span
              style={
                diff.accessories.missing.length === 0 && diff.accessories.extra.length === 0
                  ? diffStyles.good
                  : diffStyles.bad
              }
            >
              {diff.accessories.missing.length === 0 && diff.accessories.extra.length === 0
                ? 'Matches expected'
                : 'Differences detected'}
            </span>
            {accessoriesMissing && (
              <div style={diffStyles.detail}>Missing: {accessoriesMissing}</div>
            )}
            {accessoriesExtra && (
              <div style={diffStyles.detail}>Unexpected: {accessoriesExtra}</div>
            )}
          </div>
          <div style={{ ...diffStyles.row, alignItems: 'flex-start', flexDirection: 'column' }}>
            <span style={diffStyles.label}>Missing / forgotten items</span>
            <span style={missingRows.filter(row => (row.name || '').trim()).length === 0 ? diffStyles.good : diffStyles.bad}>
              {missingRows.filter(row => (row.name || '').trim()).length === 0
                ? 'None logged'
                : `${missingRows.filter(row => (row.name || '').trim()).length} item(s) logged`}
            </span>
            {missingRows.filter(row => (row.name || '').trim()).length > 0 && (
              <div style={diffStyles.detail}>
                {missingRows.filter(row => (row.name || '').trim()).map(row => `${row.name.trim()}${Number(row.qty) > 1 ? ` ×${row.qty}` : ''}`).join(', ')}
              </div>
            )}
          </div>
        </section>

        <RmaCaseImages
          rma={rma}
          stage="received"
          title="Intake photos"
          helper="Photograph the unopened package, how the unit arrived, and any damage found. Stored on the case and shown in the history."
          onSave={onSave}
        />

        {error && <p style={sectionStyles.error}>{error}</p>}

        <SubmissionImageGallery images={rma.submissionImages} rmaId={rma._id} canCategorize={true} />

        <div style={sectionStyles.footer}>
          <button onClick={handleConfirm} style={styles.button} disabled={saving}>
            {saving ? 'Saving…' : 'Confirm intake'}
          </button>
          <button onClick={onClose} style={styles.buttonAlt} disabled={saving}>
            Cancel
          </button>
        </div>
        <div style={{ marginTop: '0.75rem', textAlign: 'right' }}>
          <CloseCaseAction rma={rma} onSave={onSave} />
        </div>
      </div>
    </div>
  );
}

const sectionStyles = {
  container: {
    border: `1px solid ${colors.panelBorder}`,
    borderRadius: '16px',
    padding: '1rem',
    marginTop: '0.75rem',
    backgroundColor: colors.surfaceRaised,
    color: colors.textBody,
    textAlign: 'left'
  },
  heading: {
    margin: '0 0 0.5rem',
    fontSize: '1rem',
    color: colors.textStrong
  },
  list: {
    margin: 0,
    paddingLeft: '1rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem'
  },
  label: {
    display: 'block',
    margin: '0.5rem 0 0.25rem',
    fontWeight: 600,
    color: colors.textStrong
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    margin: '0.5rem 0 0.25rem',
    fontWeight: 500,
    color: colors.textBody
  },
  helperText: {
    fontSize: '0.8rem',
    color: colors.textDim,
    marginBottom: '0.35rem'
  },
  input: {
    width: '100%',
    borderRadius: '10px',
    border: `1px solid ${colors.panelBorder}`,
    padding: '0.6rem 0.75rem',
    backgroundColor: 'rgba(255,255,255,0.04)',
    color: colors.textStrong
  },
  accessoryTable: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.35rem'
  },
  accessoryHeader: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 2fr) 100px 110px 90px 40px',
    gap: '0.5rem',
    fontWeight: 600,
    fontSize: '0.85rem',
    color: colors.textStrong
  },
  accessoryRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 2fr) 100px 110px 90px 40px',
    gap: '0.5rem',
    alignItems: 'center'
  },
  accessoryCell: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
    fontSize: '0.85rem'
  },
  codeInput: {
    width: '100%',
    borderRadius: '10px',
    border: `1px solid ${colors.panelBorder}`,
    padding: '0.5rem 0.65rem',
    backgroundColor: 'rgba(255,255,255,0.04)',
    color: colors.textStrong
  },
  qtyInput: {
    width: '100%',
    borderRadius: '10px',
    border: `1px solid ${colors.panelBorder}`,
    padding: '0.5rem 0.65rem',
    backgroundColor: 'rgba(255,255,255,0.04)',
    color: colors.textStrong
  },
  selectInput: {
    width: '100%',
    borderRadius: '10px',
    border: `1px solid ${colors.panelBorder}`,
    padding: '0.5rem 0.65rem',
    backgroundColor: 'rgba(255,255,255,0.04)',
    color: colors.textStrong
  },
  accessoryHint: {
    fontSize: '0.75rem',
    color: colors.textDim
  },
  accessoryEmpty: {
    fontSize: '0.85rem',
    color: colors.textDim,
    padding: '0.35rem 0'
  },
  removeCell: {
    display: 'flex',
    justifyContent: 'center'
  },
  removeButton: {
    border: 'none',
    backgroundColor: 'transparent',
    color: colors.textDim,
    cursor: 'pointer',
    fontSize: '1rem'
  },
  addButton: {
    marginTop: '0.5rem',
    border: 'none',
    backgroundColor: 'transparent',
    color: colors.accent,
    cursor: 'pointer',
    fontWeight: 600
  },
  error: {
    marginTop: '0.75rem',
    color: colors.danger,
    fontWeight: 600
  },
  footer: {
    marginTop: '1rem',
    display: 'flex',
    gap: '0.5rem',
    justifyContent: 'flex-end'
  }
};

const diffStyles = {
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.35rem',
    fontSize: '0.9rem'
  },
  label: {
    fontWeight: 600
  },
  good: {
    color: colors.accent,
    fontWeight: 600
  },
  bad: {
    color: colors.danger,
    fontWeight: 600
  },
  detail: {
    fontSize: '0.8rem',
    color: colors.textMuted,
    marginTop: '0.25rem'
  }
};
